import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { usePreviewStore } from '@/stores/preview-store'

const DOCK_QUERY = '(min-width: 1280px)'

/** Preview contexts that have a docked reader somewhere in the app. */
export type DockContext = 'chat' | 'analysis'

interface DockHostState {
  /** Mounted dock hosts per context — a count, so StrictMode's double mount is safe. */
  hosts: Record<DockContext, number>
  register: (context: DockContext) => void
  unregister: (context: DockContext) => void
}

const useDockHostStore = create<DockHostState>()((set) => ({
  hosts: { chat: 0, analysis: 0 },
  register: (context) =>
    set((s) => ({ hosts: { ...s.hosts, [context]: s.hosts[context] + 1 } })),
  unregister: (context) =>
    set((s) => ({ hosts: { ...s.hosts, [context]: Math.max(0, s.hosts[context] - 1) } })),
}))

/**
 * Declares that the calling page renders a docked reader (<ChatDocumentPanel>)
 * for `context` previews. Call it unconditionally in the page component —
 * not in the panel, which only mounts once docking is already decided.
 *
 * Docking is opt-in per page: a preview opened where no host is mounted (the
 * section editor's AI panel, Strategy's Bid Brief) must open the modal at any
 * width, or the modal self-hides and nothing ever appears.
 */
export function useDockHost(context: DockContext, enabled = true): void {
  useEffect(() => {
    if (!enabled) return
    const { register, unregister } = useDockHostStore.getState()
    register(context)
    return () => unregister(context)
  }, [context, enabled])
}

function useHasDockHost(context: DockContext): boolean {
  return useDockHostStore((s) => s.hosts[context] > 0)
}

/**
 * True on viewports wide enough to dock a panel beside the chat column.
 *
 * Shared by every surface that claims that slot — the citation preview
 * (below) and the quick-write artifact panel — so they agree on one
 * breakpoint rather than each running their own matchMedia.
 */
export function useWideViewport(): boolean {
  const [isWide, setIsWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(DOCK_QUERY).matches
      : false
  )

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(DOCK_QUERY)
    const handler = (e: MediaQueryListEvent) => setIsWide(e.matches)
    setIsWide(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isWide
}

/**
 * True when the chat citation preview should dock as a side panel
 * (open + chat context + wide viewport + a chat dock host mounted) rather
 * than open full-screen.
 *
 * The chat surfaces render <ChatDocumentPanel> on this, and the global
 * <DocumentPreviewModal> self-hides on it — both derive the same boolean
 * from global signals, so they never both claim the preview.
 */
export function useDockedPreview(): boolean {
  const isOpen = usePreviewStore((s) => s.isOpen)
  const context = usePreviewStore((s) => s.context)
  const isWide = useWideViewport()
  const hasHost = useHasDockHost('chat')

  return isOpen && context === 'chat' && isWide && hasHost
}

/**
 * True when an Analysis-tab citation preview should dock beside the content
 * rather than open full-screen — so a requirement drawer isn't buried under a
 * modal every time the user checks a source.
 *
 * Only Analysis surfaces set context 'analysis', so Library and Strategy
 * previews (context 'library') keep the modal: nothing renders a docked reader
 * on those pages, and docking there would make previews vanish.
 */
export function useDockedAnalysisPreview(): boolean {
  const isOpen = usePreviewStore((s) => s.isOpen)
  const context = usePreviewStore((s) => s.context)
  const isWide = useWideViewport()
  const hasHost = useHasDockHost('analysis')

  return isOpen && context === 'analysis' && isWide && hasHost
}

